<template>
    <div :class="[sizeClass, focusClass, lowContrastClasses]">
        <template v-if="$store && !$store.state.hideApp">
            <component :is="layout" v-if="routerReady && layoutDataReady"/>

            <div class="portuna-application-external-scope">
                <div id="portal-target-popups"></div>
                <div id="portal-target-chat"></div>
                <app-light-box v-if="lightBoxOpen && currentUser.id"/>
                <top-message/>
                <app-toolbar v-if="$route.name && !isBlankLayout && showToolbar && !isExportRequest"/>
            </div>
        </template>

        <div class="load-backdrop" v-if="$store && loadingIndicator.active">
            <div class="popup-load">
                <loading-indicator :text="null" :spinner-style="{width: '122px', height: '122px'}"/>
                <div class="text" v-if="loadingIndicator.text">{{loadingIndicator.text}}</div>
            </div>
        </div>

        <app-popup v-for="(popup, key) of popups" :key="key" v-bind="popup"/>
    </div>
</template>

<script lang="ts">
    import {unwatchResize, watchResize} from '@/library/onResize';
    import {Component, Vue, Watch} from 'vue-facing-decorator';
    import {namespace, State} from 'vuex-facing-decorator';
    import AppToolbar from './components/AppToolbar.vue';
    import AppPopup from './components/AppPopup.vue';
    import LayoutMinimal from './components/LayoutMinimal.vue';
    import LoadingIndicator from './components/LoadingIndicator.vue';
    import TopMessage from './components/TopMessage.vue';
    import {checkContrast, getHex} from './library/contrast';
    import {identifyFixedHeaderElement, scrollToContentElement, scrollToTop} from './library/dom';
    import {exportEnabled} from './library/export';
    import {getSizeClass} from './library/mediaQueryHandler';
    import {defineAsyncComponentWrapper, isEqual} from './library/utils';
    import {initializeBuildUpdate} from './router';

    const LayoutMain   = defineAsyncComponentWrapper(() => import('./components/LayoutMain.vue'));
    const LayoutBlank  = defineAsyncComponentWrapper(() => import('./components/LayoutBlank.vue'));
    const LayoutExport = defineAsyncComponentWrapper(() => import('./components/LayoutExport.vue'));

    const LightBox = namespace('lightBox');

    @Component({
        name: 'App',
        components: {
            AppLightBox: defineAsyncComponentWrapper(() => import('./components/AppLightBox.vue')),
            AppToolbar,
            TopMessage,
            LoadingIndicator,
            LayoutMain,
            LayoutBlank,
            LayoutExport,
            LayoutMinimal,
            AppPopup,
        },
    })
    export default class App extends Vue
    {
        @LightBox.Getter('open') lightBoxOpen: boolean;

        @State('layoutDataLoaded') layoutDataReady: boolean;

        @State('loader') loadingIndicator: any;

        @State('loggedIn') loggedIn: boolean;

        @State('showToolbar') showToolbar: boolean;

        @State('layoutData') layoutData: LayoutData;

        @State('popups') popups: Record<string, PopupOptions>;

        lastEvent = '';

        routerReady: boolean = false;
        
        resizeObserver: Nullable<PortUNAResizeObserver> = null;

        resizeObserverUnwatchTimeout: Nullable<number> = null;

        get layout()
        {
            let layout;

            if (exportEnabled())
            {
                layout = 'layout-export';
            }
            else if (this.isBlankLayout)
            {
                layout = 'layout-blank';
            }
            else if (!this.loggedIn || this.isMinimalLayout)
            {
                layout = 'layout-minimal';
            }
            else
            {
                layout = 'layout-main';
            }

            return layout;
        }

        get focusClass()
        {
            if ('tab' === this.lastEvent)
            {
                return 'show-focus';
            }

            return '';
        }

        get lowContrastClasses()
        {
            if (!this.layoutData || !this.layoutData.colors)
            {
                return [];
            }

            const classes = [];
            for (let color of ['primary', 'secondary'])
            {
                if (this.layoutData.colors[`color-${color}`])
                {
                    const hex = getHex(this.layoutData.colors[`color-${color}`]);

                    if (!hex)
                    {
                        continue;
                    }

                    if (!checkContrast(hex, '#f6f6f6'))
                    {
                        classes.push(`low-contrast${'primary' === color ? '' : `-${color}`}`);
                    }
                }
            }

            return classes;
        }

        get sizeClass(): string
        {
            return getSizeClass(this.viewPortSize, 'window-');
        }

        get showChat()
        {
            return this.currentUser && this.currentUser.hasChat;
        }

        get isBlankLayout()
        {
            return this.$route && this.$route.meta.layout === 'blank';
        }

        get isMinimalLayout()
        {
            return this.$route && this.$route.meta.layout === 'minimal';
        }

        @Watch('isBlankLayout')
        watchBlankLayout()
        {
            if (this.isBlankLayout)
            {
                document.documentElement.classList.add('blank-layout');
            }
            else
            {
                document.documentElement.classList.remove('blank-layout');
            }
        }

        created()
        {
            window.addEventListener('mousedown', () =>
            {
                this.lastEvent = 'mouse';
            });

            window.addEventListener('keydown', (e) =>
            {
                this.lastEvent = 'Tab' === e.code ? 'tab' : this.lastEvent;
            });

            // would otherwise initially resolve "/" path which would result in multiple layout changes and thus unwanted component-loads
            this.$router.isReady().then(() => {
                this.routerReady = true;
            });
        }

        mounted()
        {
            initializeBuildUpdate();

            const fixedHeaderElement = identifyFixedHeaderElement([this.$el]);

            let isInitialized        = false;

            this.$router.beforeEach((to, from) =>
            {
                // prevent first scroll
                if (this.isExportRequest && !isInitialized)
                {
                    if (!from.name)
                    {
                        return;
                    }

                    isInitialized = true;
                }

                // don't scroll @ hash change
                if (from.name !== to.name || !to.meta?.disableScrollOnParamChange && !isEqual(from.params, to.params))
                {
                    if (!this.isExportRequest)
                    {
                        this.$nextTick(scrollToTop);

                        return;
                    }

                    if (this.resizeObserverUnwatchTimeout)
                    {
                        window.clearTimeout(this.resizeObserverUnwatchTimeout);

                        this.resizeObserverUnwatchTimeout = null;
                    }

                    if (!this.resizeObserver)
                    {
                        this.resizeObserver = watchResize(this.$el, () => scrollToContentElement(this.$el, fixedHeaderElement));
                    }

                    this.$nextTick(() => scrollToContentElement(this.$el, fixedHeaderElement));

                    this.resizeObserverUnwatchTimeout = window.setTimeout(() => {
                        if (!this.resizeObserver)
                        {
                            return;
                        }

                        unwatchResize(this.resizeObserver);

                        this.resizeObserver = null;
                    }, 5000);
                }
            });

            // defined after some vue-instances to work with are present
            this.$router.afterEach(() =>
            {
                this.$store.dispatch('clearError').catch(console.error);
                this.$store.dispatch('hideApp', false);
            });
        }
    }
</script>
